/*
 * Ryan O'Dowd
 * 2023-08-02
 * © Copyright 2023 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
// @TODO: should this be used in Settings as well?
import React, {
  useEffect,
  useState,
} from 'react';
import {
  ScrollView,
  View,
} from 'react-native';
import {
  clearLocalVersesForTranslationId,
  fetchVerses,
  updateUserSettings,
} from '@utils/store/actions';
import Button from '@components/common/Button';
import ButtonGroup from '@components/common/ButtonGroup';
import Dialog from '@components/common/Dialog';
import EmptyScreen from '@components/common/EmptyScreen';
import IconButton from '@components/common/IconButton';
import LicensingInfo from '@components/LicensingInfo';
import Searchbar from '@components/common/Searchbar';
import Text from '@components/common/Text';
import Translation from '@utils/db/models/Translation';
import VerseText from '@utils/db/models/VerseText';
import db from '@utils/db';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const MAX_NUM_FAVORITES = 8;

const TranslationSelectionDialog = (props: TranslationSelectionDialogProps) => {
  const dispatch = hooks.useTypedDispatch();
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const isAdmin = hooks.useIsAdmin();

  const [_searchText, setSearchText] = useState('');
  const [_openTranslation, setOpenTranslation] = useState(null);

  const [_translationSettingsVisible, setTranslationSettingsVisible] = useState(false);

  const [_fetchActivePassages, setFetchActivePassages] = useState(true);
  const [_isFetchingTranslationId, setIsFetchingTranslationId] = useState(null);

  const activePassages = hooks.useTypedSelector((state) => state.goals[state.settingsActiveGoalId]?.passages || [], (a, b) => JSON.stringify(a) === JSON.stringify(b)); // @TODO: get rid of stringify
  const __goalPassagesVerses = hooks.useGetVersesForPassages(activePassages, _fetchActivePassages); // @TODO: this is only here so that passages for active goals are refetched after clearing cache // @TODO: am i crazy, or should the second arg be !_fetchActivePassages? (i think the variable is just named poorly)
  // @TODO: const verses = hooks.useTypedSelector((state) => state.verses);

  const locales = hooks.useTypedSelector((state) => state.locales);
  const userSettings = hooks.useTypedSelector((state) => state.userSettings);
  const translationsByLocale = hooks.useTypedSelector((state) => {
    const selectableTranslations = state.translations.filter((t) => {
      return (t.disabled === 0 && t.downloadable === 1) || isAdmin;
    });

    const byLocale = {};
    selectableTranslations.forEach((t) => {
      if (!byLocale[t.locale_id]) {
        byLocale[t.locale_id] = [];
      }

      byLocale[t.locale_id].push(t);
    });

    Object.keys(byLocale).forEach((key) => {
      byLocale[key] = byLocale[key].sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    });

    return byLocale;
  }, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  const favoriteTranslations = hooks.useTypedSelector((state) => state.userSettings.favorite_translation_ids?.map((id) => state.translations.find((t) => t.id === id)).filter((t) => !!t) || [], (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  const sortedAndFilteredTranslations = [
    ...favoriteTranslations.map((t, i) => {
      const retVal = {...t, isFavorite: true};
      if (i === 0) {
        return {...retVal, sectionTitle: 'Favorites'}; // @TODO: i18n
      }

      return retVal;
    }),
  ];

  const localeOrder = Object.keys(translationsByLocale).sort((a, b) => {
    // ensure user's locale shows up first
    if (a === userSettings.locale_id && b !== userSettings.locale_id) return -1;
    if (b === userSettings.locale_id && a !== userSettings.locale_id) return 1;

    return +a - +b;
  });
  localeOrder.forEach((localeId) => {
    const thisLocale = locales[localeId];
    const filtered = [];
    translationsByLocale[localeId].forEach((t) => {
      if (!_searchText || [t.name, t.abbreviation, t.name_english, thisLocale.iso_639_3, thisLocale.datefns_code, thisLocale.language_name, thisLocale.language_name_local].some((val) => val.toLocaleLowerCase().includes(_searchText.toLocaleLowerCase()))) {
        filtered.push(t);
      }
    });
    sortedAndFilteredTranslations.push(...filtered);
  });

  const preferredTranslation = hooks.usePreferredTranslation();

  const [_downloadedTranslationId, setDownloadedTranslationId] = useState(null);
  const [_deletedTranslationId, setDeletedTranslationId] = useState(null);

  const translations = hooks.useTypedSelector((state: any) => state.translations);
  const [_translationMetadataById, setTranslationMetadataById] = useState({});
  useEffect(() => { // @TODO: needs to be rerun on any download/cache deletion as well
    const fetchTranslationMetadata = async () => {
      const translationsInDb = await Translation.fetchAll(db);
      const verseCounts = await VerseText.numVersesDownloadedByTranslationIds(db, translations.map((t) => t.id));

      setTranslationMetadataById(translations.reduce((obj, t) => {
        obj[t.id] = {
          ...t,
          isFullDownload: translationsInDb.find((t2) => t2.translationId === t.id)?.isFullDownload || false,
          numVersesDownloaded: verseCounts[t.id],
          humanReadableSize: utilities.humanReadableBytes(t.size_in_bytes * (verseCounts[t.id] / t.total_number_of_verses)),
        };
        return obj;
      }, {}));
    };

    fetchTranslationMetadata();
  }, [translations, _downloadedTranslationId, _deletedTranslationId]);

  useEffect(() => {
    const foo = async () => {
      await Translation.upsertAll(db, [{translation_id: _downloadedTranslationId, isFullDownload: true}]);

      setDownloadedTranslationId(null); // @TODO: here to make sure that translations are refetched from db in useEffect above
    };

    if (_downloadedTranslationId) {
      foo();
    }
  }, [_downloadedTranslationId]);

  useEffect(() => { // @TODO: don't like having this
    const foo = async () => {
      await Translation.upsertAll(db, [{translation_id: _deletedTranslationId, isFullDownload: false}]);

      setDeletedTranslationId(null); // @TODO: here to make sure that translations are refetched from db in useEffect above
    };

    if (_deletedTranslationId) {
      foo();
    }
  }, [_deletedTranslationId]);

  if (!preferredTranslation) { // @TODO: shouldn't need this, but getting errors on localhost without it...
    return null;
  }

  const fetchVersesFun = (translationId) => { // @TODO: rename
    if (!_translationMetadataById[translationId]?.isFullDownload) {
      setIsFetchingTranslationId(translationId);
      dispatch(fetchVerses(translationId, undefined, undefined, undefined, undefined, async () => {
        setIsFetchingTranslationId(null);
        setDownloadedTranslationId(translationId);
      }));
    }
  };

  return (
    <Dialog
      style={styles.bar}
      visible={props.isOpen}
      onDismiss={() => props.onClose(preferredTranslation.id)}
    >
      <Dialog.Title style={styles.foo}>
        <View style={styles.titleWrapper}>{/* @TODO: why can't this be part of Dialog.Title styles? */}
          <Text style={styles.titleText}>{props.title || i18n('translation')}</Text>
          {!props.hideSettingsToggle &&
            <View style={styles.rightActions}>
              <IconButton
                icon='cog'
                size={20 * styles.globalScale}
                onPress={() => setTranslationSettingsVisible((curr) => !curr)}
              />
            </View>
          }
        </View>
      </Dialog.Title>
      {/* @TODO: can't figure out what is causing the gap between these items */}
      <Dialog.Content disableScroll={true}>
        {props.description}
        <Searchbar
          containerStyles={styles.searchbar}
          whiteVariant={true}
          onChangeText={(searchText) => setSearchText(searchText)}
          value={_searchText}
          autoCorrect={false}// there are too many words that make auto-correct difficult and confusing
          placeholder={i18n('searchTranslations')}
          autoFocus={false}
        />
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={_translationSettingsVisible && styles.dialogContentInnerSettingsOpen()}
        >
          {sortedAndFilteredTranslations.length === 0 &&
            <EmptyScreen.Wrapper avoidKeyboard={true}>
              <EmptyScreen.Text extraStyle={styles.emptyText}>
                {i18n('noMatches', _searchText.trim())}
              </EmptyScreen.Text>
            </EmptyScreen.Wrapper>
          }
          <View>
            {_translationSettingsVisible && <Text>Select favorites for quicker translation selection and for comparing verses in the Read screen.</Text>}{/* @TODO: i18n */}
            <ButtonGroup
              options={sortedAndFilteredTranslations.map((t, index) => {
                const thisTranslationIsFavorite = userSettings.favorite_translation_ids?.includes(t.id);
                const option: any = {
                  title: `${t.name} (${t.abbreviation})`,
                  value: t.id,
                  textStyle: styles.optionText,
                  leftItemInner: _translationSettingsVisible && (
                    <IconButton
                      style={styles.translationSettingsIcon}
                      disabled={!thisTranslationIsFavorite && userSettings.favorite_translation_ids?.length > MAX_NUM_FAVORITES}
                      icon={thisTranslationIsFavorite ? 'star' : 'star-outline'}
                      iconColor={thisTranslationIsFavorite ? (styles.isDarkMode ? styles.colors.primaryLight : styles.colors.primary) : styles.colors.gray}
                      size={20 * styles.globalScale}
                      onPress={() => dispatch(updateUserSettings({favorite_translation_ids: thisTranslationIsFavorite ? userSettings.favorite_translation_ids?.filter((id) => id !== t.id) : [...userSettings.favorite_translation_ids, t.id]}))}
                    />
                  ),
                  rightItemInner: _translationSettingsVisible && (
                    <IconButton
                      style={styles.translationSettingsIcon}
                      // @TODO: disabled={_translationMetadataById[t.id]?.isFullDownload}// @TODO: disabling it changes the color
                      disabled={_isFetchingTranslationId === t.id}
                      loading={_isFetchingTranslationId === t.id}
                      icon={_translationMetadataById[t.id]?.isFullDownload ? 'cloud-check-variant-outline' : 'cloud-download'}// @TODO: there should be a progress indicator here (and repress disabled) while this is being downloaded
                      iconColor={_translationMetadataById[t.id]?.isFullDownload ? styles.colors.primaryExtraDark : (styles.isDarkMode ? styles.colors.primaryLight : styles.colors.primary)}
                      size={20 * styles.globalScale}
                      onPress={() => fetchVersesFun(t.id)}
                    />
                  ),
                  rightItem: _translationSettingsVisible && (
                    <IconButton
                      style={styles.translationSettingsIcon}
                      icon='cog'
                      iconColor={styles.colors.gray}
                      size={20 * styles.globalScale}
                      onPress={() => setOpenTranslation(t)}
                    />
                  ),
                };

                if (t.sectionTitle) {
                  option.sectionTitle = t.sectionTitle;
                } else if (!t.isFavorite && (index === 0 || (sortedAndFilteredTranslations[index - 1].isFavorite) || (t.locale_id !== sortedAndFilteredTranslations[index - 1].locale_id))) {
                  option.sectionTitle = locales[t.locale_id]?.language_name_local;
                }

                return option;
              })}
              selectedValue={preferredTranslation.id}
              onSelect={(newPreferredTranslationId) => {
                dispatch(updateUserSettings({preferred_translation_id: newPreferredTranslationId}));
                if (props.autoClose) {
                  props.onClose(preferredTranslation.id);
                }
              }}
              isVertical={true}
            />
          </View>
        </ScrollView>
        {_openTranslation &&
          <Dialog
            visible={_openTranslation}
            onDismiss={() => setOpenTranslation(null)}
          >{/* @TODO: abstract this modal to it's own component and make available in Settings component as well */}
            {/* @TODO: should settings be tab-navigated, grouped by category? */}
            {/* @TODO: dialog too big for some screens when a11y is set to extra large */}
            <Dialog.Title>{_openTranslation.name}</Dialog.Title>
            <Dialog.Content>
              <View style={styles.sectionWrapper}>
                <View style={styles.contentWrapper}>
                  {/* @TODO: download status (available, downloaded, partially downloaded, update available) */}
                  {/* @TODO: toggle for auto update? actually, that should be default behavior */}
                  <View style={styles.section}>
                    <Text style={styles.descriptionText}>{_openTranslation.downloadable ? (_translationMetadataById[_openTranslation.id]?.isFullDownload ? i18n('downloaded', _openTranslation.abbreviation) : i18n('downloadInstructions')) : i18n('notDownloadable')}</Text>
                    <View style={styles.buttonAndSizeWrapper}>
                      <Button
                        mode='contained'
                        icon='download'
                        disabled={_isFetchingTranslationId === _openTranslation.id || !_openTranslation?.downloadable || _translationMetadataById[_openTranslation.id]?.isFullDownload}
                        loading={_isFetchingTranslationId === _openTranslation.id}
                        color={styles.colors.primary}
                        onPress={() => fetchVersesFun(_openTranslation.id)}
                      >
                        {_translationMetadataById[_openTranslation.id]?.isFullDownload ? i18n('versionDownloaded', _openTranslation.abbreviation) : i18n('downloadVersion', _openTranslation.abbreviation)}
                      </Button>
                      <Text style={styles.sizeText}>{utilities.humanReadableBytes(_openTranslation.size_in_bytes)}</Text>
                    </View>
                  </View>
                  <View style={styles.gap} />
                  <View style={styles.section}>
                    {_translationMetadataById[_openTranslation.id].numVersesDownloaded !== 0 && <Text style={styles.descriptionText}>{i18n('deleteInstructions')}</Text>}
                    <View style={styles.buttonAndSizeWrapper}>
                      <Button
                        mode='contained'
                        icon='trash-can'
                        disabled={!_translationMetadataById[_openTranslation.id]?.numVersesDownloaded}
                        loading={!!_deletedTranslationId}
                        color={styles.colors.alert}
                        onPress={() => {
                          dispatch(clearLocalVersesForTranslationId(_openTranslation.id)); // @TODO: this will do the passage of the day as well...refetch it?
                          setDeletedTranslationId(_openTranslation.id); // @TODO: optimistic rendering...not technically deleted yet unless i add a callback to the clearLocalVersesForTranslationId call
                          setFetchActivePassages(false);
                          setTimeout(() => setFetchActivePassages(true), 1000); // in case we clear the cache twice in a row @TODO: not ideal, but there's a race condition clearing the redux store
                        }}
                      >
                        {i18n('deleteLocalData')}
                      </Button>
                      <Text style={styles.sizeText}>{_translationMetadataById[_openTranslation.id].humanReadableSize}</Text>{/* @TODO: i18n */}
                    </View>
                  </View>
                  <View style={styles.gap} />
                  <LicensingInfo translationId={_openTranslation.id} />
                </View>
              </View>
            </Dialog.Content>
            <Dialog.Actions>
              <Button
                mode='contained'
                onPress={() => setOpenTranslation(null)}
              >
                {i18n('done')}
              </Button>
            </Dialog.Actions>
          </Dialog>
        }
      </Dialog.Content>
      {!!props.closeButtonText &&
        <Dialog.Actions>
          <Button
            mode='contained'
            disabled={!preferredTranslation.id || props.continueButtonDisabledWithActivityIndicator}
            onPress={() => props.onClose(preferredTranslation.id)}
            loading={props.continueButtonDisabledWithActivityIndicator}
          >
            {props.continueButtonDisabledWithActivityIndicator
              ? 'Loading'
              : props.closeButtonText || i18n('close')
            }
          </Button>
        </Dialog.Actions>
      }
      {/* @TODO: show close/cancel button if user doesn't want to pick a new one? */}
    </Dialog>
  );
};

class TranslationSelectionDialogProps {
  isOpen: boolean;
  onClose: (translationId: number) => void;

  autoClose?: boolean;
  hideSettingsToggle?: boolean;
  continueButtonDisabledWithActivityIndicator?: boolean;
  title?: string;
  description?: any;
  closeButtonText?: any;
};

export default TranslationSelectionDialog;
